import React, { useMemo } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Search, Building } from 'lucide-react';

const ContactSelectorModal = ({
    isOpen,
    onClose,
    organizations = [],
    newCampaign,
    setNewCampaign,
    orgSearchTerm,
    setOrgSearchTerm
}) => {
    const selectedContacts = newCampaign.selectedContacts || [];

    
    const filteredOrganizations = organizations;

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="w-full  !max-w-[825px] max-h-[80vh]" style={{ zIndex: 1000 }}>
                <DialogHeader>
                    <DialogTitle>Select Contacts for Campaign</DialogTitle>
                    <DialogDescription>
                        Choose which contacts to include in your campaign. You can select individual contacts or entire organizations.
                    </DialogDescription>
                </DialogHeader>
                <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Search organizations or contacts..."
                        className="pl-9"
                        value={orgSearchTerm}
                        onChange={(e) => setOrgSearchTerm(e.target.value)}
                    />
                </div>
                <ScrollArea className="h-[450px] custom-scrollbar">
                    <div className="space-y-2" style={{paddingBottom:60}}>
                        {filteredOrganizations.length === 0 ? (
                            <p className="text-center text-muted-foreground">No organizations or contacts found.</p>
                        ) : (
                            filteredOrganizations.map((org) => (
                                <Card key={org.id} className="p-4">
                                    <div className="flex items-center justify-between mb-3">
                                        <h4 className="font-medium flex items-center">
                                            <Building className="mr-2 h-4 w-4" />
                                            {org.name}
                                        </h4>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => {
                                                const orgContactIds = org.contacts.map(c => c.id);
                                                const allSelected = orgContactIds.every(id => selectedContacts.includes(id));
                                                if (allSelected) {
                                                    setNewCampaign(prev => ({
                                                        ...prev,
                                                        selectedContacts: prev.selectedContacts.filter(id => !orgContactIds.includes(id))
                                                    }));
                                                } else {
                                                    setNewCampaign(prev => ({
                                                        ...prev,
                                                        selectedContacts: [...new Set([...prev.selectedContacts, ...orgContactIds])]
                                                    }));
                                                }
                                            }}
                                        >
                                            {org.contacts.every(c => selectedContacts.includes(c.id)) ? 'Deselect All' : 'Select All'}
                                        </Button>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {org.contacts.map((contact) => (
                                            <div key={contact.id} className="flex items-center space-x-3">
                                                <Checkbox
                                                    id={`contact-${contact.id}`}
                                                    checked={selectedContacts.includes(contact.id)}
                                                    onCheckedChange={(checked) => {
                                                        if (checked) {
                                                            setNewCampaign(prev => ({
                                                                ...prev,
                                                                selectedContacts: [...prev.selectedContacts, contact.id]
                                                            }));
                                                        } else {
                                                            setNewCampaign(prev => ({
                                                                ...prev,
                                                                selectedContacts: prev.selectedContacts.filter(id => id !== contact.id)
                                                            }));
                                                        }
                                                    }}
                                                    className="contact-checkbox"
                                                />
                                                <div className="contact-card flex-1 p-3 border rounded-lg">
                                                    <div className="font-medium">{contact.full_name ?? contact.name ?? 'Unnamed Contact'}</div>
                                                    <div className="text-sm text-muted-foreground">{contact.title}</div>
                                                    <div className="text-sm text-muted-foreground">{contact.email}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </Card>
                            ))
                        )}
                    </div>
                </ScrollArea>
                        <div className="absolute bottom-1 left-0 right-0 border-t bg-background px-6 py-4 flex justify-between items-center">
            <div className="text-sm text-muted-foreground">
                {selectedContacts.length} contacts selected
            </div>
            <div className="space-x-2">
                <Button variant="outline" onClick={onClose}>
                    Cancel
                </Button>
                <Button onClick={onClose}>
                    Confirm Selection
                </Button>
            </div>
        </div>
            </DialogContent>
        </Dialog>
    );
};

export default ContactSelectorModal;